import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationAPI } from '../../services/api';
import { useTheme } from '../../ThemeContext';

const NotificationBell = ({ onNavigateToProject }) => {
    const { isDark } = useTheme();
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [clearing, setClearing] = useState(false);
    const dropdownRef = useRef(null);
    const PAGE_SIZE = 50;

    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await notificationAPI.getUnreadCount();
            setUnreadCount(response.data.count);
        } catch (err) {
            console.error('Error fetching unread count:', err);
        }
    }, []);

    // Poll unread count every 30s
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleBellClick = async () => {
        const opening = !isOpen;
        setIsOpen(opening);
        if (opening) {
            setLoading(true);
            setPage(1);
            try {
                const response = await notificationAPI.getAll({ page: 1, pageSize: PAGE_SIZE });
                setNotifications(response.data.items || []);
                setHasMore(!!response.data.hasMore);
            } catch (err) {
                console.error('Error fetching notifications:', err);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleLoadMore = async () => {
        const nextPage = page + 1;
        setLoadingMore(true);
        try {
            const response = await notificationAPI.getAll({ page: nextPage, pageSize: PAGE_SIZE });
            setNotifications(prev => [...prev, ...(response.data.items || [])]);
            setHasMore(!!response.data.hasMore);
            setPage(nextPage);
        } catch (err) {
            console.error('Error loading more notifications:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleClearAll = async () => {
        if (!window.confirm('Clear all notifications? This cannot be undone.')) return;
        setClearing(true);
        try {
            await notificationAPI.clearAll();
            setNotifications([]);
            setUnreadCount(0);
            setHasMore(false);
            setPage(1);
        } catch (err) {
            console.error('Error clearing notifications:', err);
        } finally {
            setClearing(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.isRead) {
            try {
                await notificationAPI.markRead(notification.id);
                setNotifications(prev => prev.map(n =>
                    n.id === notification.id ? { ...n, isRead: true } : n
                ));
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (err) {
                console.error('Error marking notification read:', err);
            }
        }
        setIsOpen(false);
        if (onNavigateToProject) {
            onNavigateToProject(notification.projectId);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await notificationAPI.markAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Error marking all read:', err);
        }
    };

    const formatRelativeTime = (dateStr) => {
        const now = new Date();
        const date = new Date(dateStr);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const displayCount = unreadCount > 9 ? '9+' : unreadCount;

    return (
        <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
                onClick={handleBellClick}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center'
                }}
                title="Notifications"
            >
                <i className="fas fa-bell" style={{ color: '#f0f0f0', fontSize: '18px' }} />
                {unreadCount > 0 && (
                    <span style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: '#e74c3c',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 700,
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1
                    }}>
                        {displayCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notif-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    width: '360px',
                    maxHeight: '420px',
                    background: isDark ? '#242930' : 'white',
                    borderRadius: '8px',
                    boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.2)',
                    border: isDark ? '1px solid #3a4049' : 'none',
                    zIndex: 1000,
                    overflow: 'hidden',
                    marginTop: '8px'
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '12px 16px',
                        borderBottom: isDark ? '1px solid #3a4049' : '1px solid #eee',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <span style={{ fontWeight: 700, color: isDark ? '#e8eaed' : '#333', fontSize: '0.95rem' }}>Notifications</span>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllRead}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--accent-primary)',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Mark all read
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    onClick={handleClearAll}
                                    disabled={clearing}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: clearing ? (isDark ? '#5f666e' : '#aaa') : '#dc3545',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        cursor: clearing ? 'not-allowed' : 'pointer',
                                        padding: 0,
                                    }}
                                    title="Delete all your notifications"
                                >
                                    {clearing ? 'Clearing…' : 'Clear all'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* List */}
                    <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                        {loading ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#8b929a' : '#999' }}>
                                Loading...
                            </div>
                        ) : notifications.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#8b929a' : '#999', fontSize: '0.85rem' }}>
                                No notifications yet
                            </div>
                        ) : (
                            notifications.map(n => (
                                <div
                                    key={n.id}
                                    onClick={() => handleNotificationClick(n)}
                                    style={{
                                        padding: '12px 16px',
                                        borderBottom: isDark ? '1px solid #333940' : '1px solid #f0f0f0',
                                        cursor: 'pointer',
                                        backgroundColor: n.isRead
                                            ? (isDark ? '#242930' : 'white')
                                            : (isDark ? '#1c2d4a' : '#f0f7ff'),
                                        transition: 'background 0.15s'
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = n.isRead
                                            ? (isDark ? '#2e343c' : '#f8f9fa')
                                            : (isDark ? '#253550' : '#e3eefa');
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor = n.isRead
                                            ? (isDark ? '#242930' : 'white')
                                            : (isDark ? '#1c2d4a' : '#f0f7ff');
                                    }}
                                >
                                    {/* Render based on notification type. NoteTag is the legacy
                                        path; TaskAssigned and StatusChanged use the pre-rendered
                                        Message field. */}
                                    {n.type === 'NoteTag' ? (
                                        <>
                                            <div style={{ fontSize: '0.85rem', color: isDark ? '#e8eaed' : '#333', lineHeight: 1.4 }}>
                                                <i className="fas fa-comment-dots" style={{ color: 'var(--accent-primary)', marginRight: '6px' }}></i>
                                                <strong>{n.authorUsername}</strong> mentioned you in a note on <strong>{n.projectName}</strong>
                                            </div>
                                            {n.notePreview && (
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    color: isDark ? '#8b929a' : '#777',
                                                    marginTop: '4px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    "{n.notePreview}"
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ fontSize: '0.85rem', color: isDark ? '#e8eaed' : '#333', lineHeight: 1.4 }}>
                                            <i
                                                className={`fas ${n.type === 'TaskAssigned' ? 'fa-tasks' : n.type === 'JobAssigned' ? 'fa-user-check' : 'fa-exchange-alt'}`}
                                                style={{ color: 'var(--accent-primary)', marginRight: '6px' }}
                                            ></i>
                                            {n.message || `Update on ${n.projectName}`}
                                        </div>
                                    )}
                                    <div style={{ fontSize: '0.75rem', color: isDark ? '#5f666e' : '#aaa', marginTop: '4px' }}>
                                        {formatRelativeTime(n.createdAt)}
                                    </div>
                                </div>
                            ))
                        )}
                        {hasMore && !loading && (
                            <button
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: 'none',
                                    border: 'none',
                                    borderTop: isDark ? '1px solid #333940' : '1px solid #f0f0f0',
                                    color: 'var(--accent-primary)',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                                    textAlign: 'center',
                                }}
                            >
                                {loadingMore ? 'Loading…' : 'Load more'}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
